import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LookupViewModel } from '../../models/wholesaler-api-models';
import { ProgramEnum } from '@program-suite/data-common/program';

@Injectable({
  providedIn: 'root',
})
export class LookupService {
  http = inject(HttpClient);
  controller = 'lookup';

  async getLookup(name: string, id?: number): Promise<LookupViewModel[]> {
    try {
      return await firstValueFrom(this.http.get<LookupViewModel[]>(`lookup/${name}${id ? `/${id}` : ''}`));
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  async getProgramStates(programCode: string, effectiveDate: string): Promise<LookupViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<LookupViewModel[]>(`lookup/appetite/states/${programCode}/${effectiveDate}`),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  async getProgramDeductibles(
    programCode: string,
    stateCode: string,
    effectiveDate: string,
  ): Promise<LookupViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<LookupViewModel[]>(`lookup/appetite/deductibles/${programCode}/${stateCode}/${effectiveDate}`),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  async getProgramLimits(programCode: string, stateCode: string, effectiveDate: string): Promise<LookupViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<LookupViewModel[]>(`lookup/appetite/limits/${programCode}/${stateCode}/${effectiveDate}`),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  async getProgramClasses(programCode: string, stateCode: string, effectiveDate: string): Promise<LookupViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<LookupViewModel[]>(`lookup/appetite/classes/${programCode}/${stateCode}/${effectiveDate}`),
      );
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  async getAgenciesBySubmission(submissionGuid: string): Promise<LookupViewModel[]> {
    try {
      return await firstValueFrom(this.http.get<LookupViewModel[]>(`lookup/agencies/submission/${submissionGuid}`));
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }

  /**
   * If we ever need to include CSRs in a lookup with agents, we can use this method.
   * @param id 
   * @returns LookupViewModel[] of agents and CSRs for the given agencyId 
   */
  async getAgentsAndCSRs(id?: number): Promise<LookupViewModel[]> {
    try {
      return await firstValueFrom(this.http.get<LookupViewModel[]>(`lookup/agents${id ? `/${id}` : ''}`, { params: { includeCSR: 'true' } }));
    } catch (error) {
      throw new Error('Something went wrong, please try again');
    }
  }
}
